import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  ShoppingCart,
  Loader2,
  Search,
  Trash2,
  Pencil,
  PackageCheck,
  Clock3,
  Euro,
  Truck,
  Minus,
  PackagePlus,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  supplier_id: "",
  order_date: new Date().toISOString().slice(0, 10),
  expected_date: "",
  supplier_reference: "",
  notes: "",
  status: "borrador",
  items: [],
};

function eur(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
}

const STATUS = {
  borrador: {
    label: "Borrador",
    className: "bg-[#F5F5F7] text-[#6E6E73]",
  },
  pedido: {
    label: "Pedido",
    className: "bg-[#EAF2FC] text-[#0066CC]",
  },
  recibido: {
    label: "Recibido",
    className: "bg-[#E9F9EE] text-[#248A3D]",
  },
  cancelado: {
    label: "Cancelado",
    className: "bg-[#FFEBEA] text-[#D70015]",
  },
};

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

export default function Purchases() {
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [receivingId, setReceivingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);

    try {
      const [
        ordersResponse,
        suppliersResponse,
        productsResponse,
      ] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/suppliers"),
        api.get("/inventory/products"),
      ]);

      setOrders(ordersResponse.data || []);
      setSuppliers(suppliersResponse.data || []);
      setProducts(productsResponse.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      order_date: new Date().toISOString().slice(0, 10),
      items: [],
    });
  };

  const openCreate = () => {
    reset();
    setOpen(true);
  };

  const openEdit = (order) => {
    if (order.status === "recibido") {
      toast.info("Los pedidos recibidos están bloqueados.");
      return;
    }

    setEditing(order);
    setForm({
      supplier_id: order.supplier_id || "",
      order_date: order.order_date
        ? String(order.order_date).slice(0, 10)
        : "",
      expected_date: order.expected_date
        ? String(order.expected_date).slice(0, 10)
        : "",
      supplier_reference: order.supplier_reference || "",
      notes: order.notes || "",
      status:
        order.status === "cancelado"
          ? "cancelado"
          : order.status || "borrador",
      items:
        order.items?.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_cost: item.unit_cost,
        })) || [],
    });
    setOpen(true);
  };

  const addItem = () => {
    setForm((current) => ({
      ...current,
      items: [
        ...current.items,
        {
          product_id: "",
          quantity: 1,
          unit_cost: 0,
        },
      ],
    }));
  };

  const updateItem = (index, key, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const next = { ...item, [key]: value };

        if (key === "product_id") {
          const product = products.find(
            (candidate) => candidate.id === value
          );

          if (product && !Number(next.unit_cost)) {
            next.unit_cost = Number(
              product.purchase_price || 0
            );
          }
        }

        return next;
      }),
    }));
  };

  const removeItem = (index) => {
    setForm((current) => ({
      ...current,
      items: current.items.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    }));
  };

  const formTotal = useMemo(
    () =>
      form.items.reduce(
        (total, item) =>
          total +
          (Number(item.quantity) || 0) *
            (Number(item.unit_cost) || 0),
        0
      ),
    [form.items]
  );

  const save = async (event) => {
    event.preventDefault();

    if (!form.supplier_id) {
      toast.error("Selecciona un proveedor.");
      return;
    }

    const validItems = form.items.filter(
      (item) =>
        item.product_id &&
        Number(item.quantity) > 0
    );

    if (!validItems.length) {
      toast.error("Añade al menos un producto.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        supplier_id: form.supplier_id,
        order_date: form.order_date || null,
        expected_date: form.expected_date || null,
        supplier_reference:
          form.supplier_reference.trim(),
        notes: form.notes.trim(),
        status: form.status,
        items: validItems.map((item) => ({
          product_id: item.product_id,
          quantity: Number(item.quantity),
          unit_cost: Number(item.unit_cost) || 0,
        })),
      };

      if (editing) {
        await api.put(
          `/purchase-orders/${editing.id}`,
          payload
        );
        toast.success("Pedido actualizado.");
      } else {
        await api.post("/purchase-orders", payload);
        toast.success("Pedido creado.");
      }

      setOpen(false);
      reset();
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const receive = async (order) => {
    if (
      !window.confirm(
        `¿Marcar ${order.number} como recibido? El stock aumentará automáticamente.`
      )
    ) {
      return;
    }

    setReceivingId(order.id);

    try {
      await api.post(
        `/purchase-orders/${order.id}/receive`
      );
      toast.success(
        "Pedido recibido. El inventario se ha actualizado."
      );
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setReceivingId(null);
    }
  };

  const remove = async (order) => {
    if (
      !window.confirm(
        `¿Eliminar el pedido ${order.number}?`
      )
    ) {
      return;
    }

    setDeletingId(order.id);

    try {
      await api.delete(`/purchase-orders/${order.id}`);
      toast.success("Pedido eliminado.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesSearch =
        !normalized ||
        [
          order.number,
          order.supplier_name,
          order.supplier_reference,
          order.notes,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalized);

      const matchesStatus =
        statusFilter === "todos" ||
        order.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [orders, query, statusFilter]);

  const pendingCount = orders.filter(
    (order) =>
      order.status === "borrador" ||
      order.status === "pedido"
  ).length;

  const receivedCount = orders.filter(
    (order) => order.status === "recibido"
  ).length;

  const receivedTotal = orders
    .filter((order) => order.status === "recibido")
    .reduce(
      (total, order) => total + Number(order.total || 0),
      0
    );

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Compras"
        subtitle={`${orders.length} pedidos de compra`}
        action={
          <Dialog
            open={open}
            onOpenChange={(value) => {
              setOpen(value);
              if (!value) reset();
            }}
          >
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nuevo pedido
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[94vh] max-w-4xl overflow-y-auto rounded-3xl border-none bg-white p-6">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {editing
                    ? `Editar ${editing.number}`
                    : "Nuevo pedido de compra"}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={save} className="mt-5 space-y-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Proveedor
                    </label>
                    <select
                      value={form.supplier_id}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          supplier_id: event.target.value,
                        })
                      }
                      required
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      <option value="">
                        Selecciona un proveedor
                      </option>
                      {suppliers.map((supplier) => (
                        <option
                          key={supplier.id}
                          value={supplier.id}
                        >
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Estado
                    </label>
                    <select
                      value={form.status}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          status: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    >
                      <option value="borrador">Borrador</option>
                      <option value="pedido">Pedido</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Fecha de pedido
                    </label>
                    <input
                      type="date"
                      value={form.order_date}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          order_date: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Entrega prevista
                    </label>
                    <input
                      type="date"
                      value={form.expected_date}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          expected_date: event.target.value,
                        })
                      }
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm font-medium">
                      Referencia del proveedor
                    </label>
                    <input
                      value={form.supplier_reference}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          supplier_reference:
                            event.target.value,
                        })
                      }
                      placeholder="Nº pedido proveedor, albarán..."
                      className="w-full rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-[#E8E8ED] p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium text-[#1D1D1F]">
                        Productos
                      </p>
                      <p className="mt-1 text-xs text-[#86868B]">
                        Al recibir el pedido estas cantidades entrarán en inventario.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addItem}
                      className="flex items-center gap-1.5 rounded-full bg-[#0066CC]/10 px-3 py-2 text-xs font-medium text-[#0066CC]"
                    >
                      <PackagePlus className="h-3.5 w-3.5" />
                      Añadir producto
                    </button>
                  </div>

                  {form.items.length === 0 ? (
                    <div className="mt-4 rounded-xl bg-[#F5F5F7] p-5 text-center text-sm text-[#86868B]">
                      Añade los productos del pedido.
                    </div>
                  ) : (
                    <div className="mt-4 space-y-3">
                      {form.items.map((item, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 gap-3 rounded-xl bg-[#F5F5F7] p-3 md:grid-cols-[minmax(0,1fr)_110px_130px_110px_40px]"
                        >
                          <div>
                            <label className="mb-1 block text-xs text-[#86868B]">
                              Producto
                            </label>
                            <select
                              value={item.product_id}
                              onChange={(event) =>
                                updateItem(
                                  index,
                                  "product_id",
                                  event.target.value
                                )
                              }
                              required
                              className="w-full rounded-lg bg-white px-3 py-2.5 text-sm outline-none"
                            >
                              <option value="">
                                Selecciona
                              </option>
                              {products.map((product) => (
                                <option
                                  key={product.id}
                                  value={product.id}
                                >
                                  {product.sku} · {product.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="mb-1 block text-xs text-[#86868B]">
                              Cantidad
                            </label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantity}
                              onChange={(event) =>
                                updateItem(
                                  index,
                                  "quantity",
                                  event.target.value
                                )
                              }
                              required
                              className="w-full rounded-lg bg-white px-3 py-2.5 text-sm outline-none"
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-xs text-[#86868B]">
                              Coste unidad
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_cost}
                              onChange={(event) =>
                                updateItem(
                                  index,
                                  "unit_cost",
                                  event.target.value
                                )
                              }
                              className="w-full rounded-lg bg-white px-3 py-2.5 text-sm outline-none"
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-xs text-[#86868B]">
                              Total
                            </label>
                            <div className="rounded-lg bg-white px-3 py-2.5 text-sm font-semibold">
                              {eur(
                                Number(item.quantity || 0) *
                                  Number(item.unit_cost || 0)
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="mt-5 flex h-10 w-10 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <div className="rounded-xl bg-[#EAF2FC] px-5 py-3 text-right">
                      <p className="text-xs text-[#0066CC]">
                        Total pedido
                      </p>
                      <p className="font-display text-xl font-bold text-[#0066CC]">
                        {eur(formTotal)}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Notas
                  </label>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        notes: event.target.value,
                      })
                    }
                    className="w-full resize-none rounded-xl bg-[#F5F5F7] px-4 py-3 outline-none"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="flex-1 rounded-full border border-[#D2D2D7] py-3 font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    {saving
                      ? "Guardando..."
                      : editing
                        ? "Guardar cambios"
                        : "Crear pedido"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={Clock3}
          label="Pendientes"
          value={pendingCount}
          color="#FF9500"
        />
        <SummaryCard
          icon={PackageCheck}
          label="Recibidos"
          value={receivedCount}
          color="#34C759"
        />
        <SummaryCard
          icon={Euro}
          label="Compras recibidas"
          value={eur(receivedTotal)}
          color="#5856D6"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <input
              value={query}
              onChange={(event) =>
                setQuery(event.target.value)
              }
              placeholder="Buscar número, proveedor o referencia..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-4 text-sm outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
            className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm outline-none"
          >
            <option value="todos">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="pedido">Pedido</option>
            <option value="recibido">Recibido</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-[#0066CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No hay pedidos de compra"
          subtitle="Crea un pedido para comenzar a gestionar las reposiciones."
        />
      ) : (
        <div className="space-y-4">
          {filtered.map((order) => {
            const status =
              STATUS[order.status] || STATUS.borrador;

            return (
              <article
                key={order.id}
                className="mantix-card p-5"
              >
                <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#EAF2FC] text-[#0066CC]">
                      <ShoppingCart className="h-5 w-5" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-mono font-semibold text-[#0066CC]">
                          {order.number}
                        </h3>
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </div>

                      <p className="mt-1 flex items-center gap-1.5 font-medium text-[#1D1D1F]">
                        <Truck className="h-4 w-4 text-[#86868B]" />
                        {order.supplier_name}
                      </p>

                      <p className="mt-1 text-xs text-[#86868B]">
                        Pedido: {order.order_date || "—"}
                        {order.expected_date
                          ? ` · Previsto: ${order.expected_date}`
                          : ""}
                      </p>
                    </div>
                  </div>

                  <div className="text-left lg:text-right">
                    <p className="font-display text-2xl font-bold text-[#1D1D1F]">
                      {eur(order.total)}
                    </p>
                    <p className="text-xs text-[#86868B]">
                      {order.items?.length || 0} líneas
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {(order.items || []).slice(0, 4).map((item) => (
                    <div
                      key={`${order.id}-${item.product_id}`}
                      className="rounded-xl bg-[#F5F5F7] p-3"
                    >
                      <p className="truncate text-sm font-medium">
                        {item.name}
                      </p>
                      <p className="mt-1 text-xs text-[#86868B]">
                        {item.quantity} {item.unit} ×{" "}
                        {eur(item.unit_cost)}
                      </p>
                    </div>
                  ))}
                </div>

                {order.supplier_reference && (
                  <p className="mt-4 text-xs text-[#86868B]">
                    Ref. proveedor: {order.supplier_reference}
                  </p>
                )}

                <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-[#E8E8ED] pt-4">
                  {order.status !== "recibido" && (
                    <button
                      type="button"
                      onClick={() => openEdit(order)}
                      className="flex items-center gap-1.5 rounded-full bg-[#F5F5F7] px-4 py-2 text-sm font-medium text-[#1D1D1F]"
                    >
                      <Pencil className="h-4 w-4" />
                      Editar
                    </button>
                  )}

                  {order.status !== "recibido" &&
                    order.status !== "cancelado" && (
                      <button
                        type="button"
                        onClick={() => receive(order)}
                        disabled={receivingId === order.id}
                        className="flex items-center gap-1.5 rounded-full bg-[#E9F9EE] px-4 py-2 text-sm font-medium text-[#248A3D] disabled:opacity-60"
                      >
                        {receivingId === order.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <PackageCheck className="h-4 w-4" />
                        )}
                        Recibir pedido
                      </button>
                    )}

                  {order.status !== "recibido" && (
                    <button
                      type="button"
                      onClick={() => remove(order)}
                      disabled={deletingId === order.id}
                      className="flex h-9 w-9 items-center justify-center rounded-full text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
                    >
                      {deletingId === order.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
